import type { ConstructorError } from '$lib/error';
import type { VoucherIdMap } from '$types/sie.type';
import { VoucherId, voucherIdSchema } from '@df/common';
import { err, ok, type Result } from 'neverthrow';
import { z } from 'zod';

const voucherStringIdSchema = z.string();
const voucherNumberSchema = z.string();
const voucherSeriesSchema = z.string();

const voucherIdTypeBareSchema = z.object({
  id: voucherIdSchema,
  series: voucherSeriesSchema,
  number: voucherNumberSchema,
});

const voucherIdTypeSchema = voucherIdTypeBareSchema.transform((id, ctx) => {
  try {
    return VoucherIdType.fromBare(id);
  } catch {
    ctx.addIssue({
      code: 'custom',
      message: `Invalid VoucherId string: ${id} could not create VoucherIdType`,
    });
    return z.NEVER;
  }
});

/**
 * A class representing a voucher id with its voucher name representation inside the class.
 *
 * Same as a VoucherIdType in the serialization layer of accounting data in Rust.
 *
 * Should be used when you need to display the voucher id in a human-readable format.
 * The inner id is the actual id used in the system and should be used as keys in maps for example.
 */
class VoucherIdType {
  private innerId: VoucherId;
  private series: string;
  private number: string;

  private constructor(args: z.infer<typeof voucherIdTypeBareSchema>) {
    this.innerId = args.id;
    this.series = args.series;
    this.number = args.number;
  }

  toJSON() {
    return this.innerId;
  }

  get id(): VoucherId {
    return this.innerId;
  }

  /** The voucher name representation, `"series-number"`. */
  display(): string {
    return `${this.series}-${this.number}`;
  }

  /**
   * Constructs a VoucherIdType from a VoucherId and a map that contains the mapping between the id and the series and number of the id.
   * Useful when you want to create a VoucherIdType from a VoucherId in the Audit application
   *
   * @param id
   * @param map Map that contains the mapping between the id and the series and number of the id.
   * @returns A Result containing the VoucherIdType if the id is valid, or an error if the id is not valid.
   */
  static fromId(id: VoucherId, map: VoucherIdMap): Result<VoucherIdType, ConstructorError> {
    const display = map.get(id);

    if (display) {
      return ok(VoucherIdType.fromBare({ id, series: display.series, number: display.number }));
    }

    return VoucherIdType.fromString(id);
  }

  static fromBare(args: z.infer<typeof voucherIdTypeBareSchema>): VoucherIdType {
    return new VoucherIdType(args);
  }

  static fromString(id: string): Result<VoucherIdType, ConstructorError> {
    const split = id.split('-');

    if (split.length !== 2) {
      return err({
        type: 'Construct',
        message: `Cannot construct 'VoucherIdType' when split: ${JSON.stringify(split)} is wrong length`,
      });
    }

    const [series, number] = split;

    return ok(new VoucherIdType({ id: VoucherId.unsafe(id), series, number }));
  }
}

export {
  VoucherIdType,
  voucherIdTypeSchema,
  voucherNumberSchema,
  voucherSeriesSchema,
  voucherStringIdSchema,
};
